import React, { useState, useEffect } from 'react'
import { Clock, Heart, Download, Share, Eye, MoreVertical, RefreshCw } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getUserImages } from '../../lib/supabase'
import { checkDatabaseStatus } from '../../lib/databaseSetup'

interface GeneratedImage {
  id: string
  title: string
  description: string
  prompt_text: string
  image_url: string
  filters_used: any
  generation_settings: {
    timestamp: string
    model: string
  }
  created_at: string
  is_public: boolean
  likes_count: number
  views_count: number
  download_count: number
}

interface GenerationHistoryProps {
  newGenerations?: GeneratedImage[]
  isGenerating?: boolean
  generationStatus?: string
}

const GenerationHistory: React.FC<GenerationHistoryProps> = ({ 
  newGenerations = [], 
  isGenerating = false, 
  generationStatus = '' 
}) => {
  const { user } = useAuth()
  const [images, setImages] = useState<GeneratedImage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null)
  const [dbStatus, setDbStatus] = useState<{
    isConnected: boolean
    tablesExist: boolean
    userProfileExists: boolean
    authStatus: string
    error?: string
  }>({ 
    isConnected: false, 
    tablesExist: false, 
    userProfileExists: false,
    authStatus: 'unknown'
  })

  useEffect(() => {
    // Reload images when user changes to ensure we get their database images
    loadUserImages()
  }, [user])

  useEffect(() => {
    // When new generations are added, merge them with existing images
    if (newGenerations.length > 0) {
      setImages(prev => {
        // Combine all images and remove duplicates
        const allImages = [...newGenerations, ...prev]
        const uniqueImages = allImages.filter((image, index, self) => 
          index === self.findIndex(img => img.id === image.id)
        )
        
        // Sort by creation date (newest first)
        const sortedImages = uniqueImages.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        
        return sortedImages
      })
    }
  }, [newGenerations])

  // Also reload when user logs in to ensure we get their database images
  useEffect(() => {
    if (user) {
      loadUserImages()
    }
  }, [user?.id])

  const loadUserImages = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      console.log('🔍 Loading user images for:', user.id)
      
      // Check database status first
      const dbStatus = await checkDatabaseStatus()
      console.log('📊 Database status:', dbStatus)
      
      // Update database status state
      setDbStatus({
        isConnected: dbStatus.existingTables.length > 0,
        tablesExist: dbStatus.missingTables.length === 0,
        userProfileExists: dbStatus.userProfileExists || false,
        authStatus: dbStatus.authStatus || 'unknown',
        error: dbStatus.missingTables.length > 0 ? `Missing tables: ${dbStatus.missingTables.join(', ')}` : undefined
      })
      
      // If database is not properly set up, show appropriate message
      if (!dbStatus.isSetup) {
        console.log('❌ Database not properly set up:', {
          missingTables: dbStatus.missingTables,
          userProfileExists: dbStatus.userProfileExists,
          authStatus: dbStatus.authStatus
        })
        
        // Still show newGenerations as fallback
        setImages(() => {
          const allImages = [...newGenerations]
          const uniqueImages = allImages.filter((image, index, self) => 
            index === self.findIndex(img => img.id === image.id)
          )
          return uniqueImages.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        })
        
        setLoading(false)
        return
      }
      
      // Database is set up, try to load user images
      const userImages = await getUserImages(user.id)
      console.log('📸 Loaded user images from database:', userImages?.length || 0)
      
      // Combine database images with new generations, avoiding duplicates
      setImages(prev => {
        const dbImages = userImages || []
        const allImages = [...newGenerations, ...dbImages]
        
        // Remove duplicates based on ID
        const uniqueImages = allImages.filter((image, index, self) => 
          index === self.findIndex(img => img.id === image.id)
        )
        
        // Sort by creation date (newest first)
        const sortedImages = uniqueImages.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        
        return sortedImages
      })
      
    } catch (error) {
      console.error('❌ Error loading user images:', error)
      
      // Update database status to show error
      setDbStatus({
        isConnected: false,
        tablesExist: false,
        userProfileExists: false,
        authStatus: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      
      // Show newGenerations as fallback
      setImages(() => {
        const allImages = [...newGenerations]
        const uniqueImages = allImages.filter((image, index, self) => 
          index === self.findIndex(img => img.id === image.id)
        )
        return uniqueImages.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      })
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getFilterSummary = (filters: any) => {
    if (!filters) return ''
    
    const activeFilters = Object.entries(filters)
      .filter(([_, value]) => value && value !== '')
      .map(([key, value]) => {
        // Format filter names to be more readable
        const formattedKey = key.replace(/([A-Z])/g, ' $1').toLowerCase()
        return `${formattedKey}: ${value}`
      })
      .slice(0, 3) // Show only first 3 filters
    
    return activeFilters.length > 0 ? activeFilters.join(', ') : ''
  }

  const handleImageClick = (image: GeneratedImage) => {
    setSelectedImage(image)
  }

  const handleDownload = async (imageUrl: string, title: string) => {
    try {
      console.log('📥 Starting image download:', { imageUrl, title })
      
      // Clean the title for filename
      const cleanTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const filename = `toefetti_${cleanTitle}.png`
      
      // Method 1: Try direct link download (works for most cases)
      const tryDirectDownload = () => {
        const link = document.createElement('a')
        link.href = imageUrl
        link.download = filename
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        
        // Add to DOM temporarily
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        console.log('✅ Direct download initiated')
        return true
      }
      
      // Method 2: Try fetch + blob (for CORS-enabled images)
      const tryFetchDownload = async () => {
        console.log('🔄 Trying fetch download method...')
        
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit'
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up blob URL
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
        
        console.log('✅ Fetch download completed')
        return true
      }
      
      // Method 3: Open in new tab as fallback
      const tryNewTabFallback = () => {
        console.log('🔄 Opening image in new tab as fallback...')
        window.open(imageUrl, '_blank', 'noopener,noreferrer')
        
        // Show user instructions
        const message = `Image opened in new tab. Right-click the image and select "Save image as..." to download it.`
        
        // Create a temporary toast notification
        const toast = document.createElement('div')
        toast.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm'
        toast.innerHTML = `
          <div class="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-0.5">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="text-sm">${message}</div>
          </div>
        `
        
        document.body.appendChild(toast)
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast)
          }
        }, 5000)
        
        console.log('✅ Fallback method used')
        return true
      }
      
      // Try methods in order
      try {
        // First try direct download
        tryDirectDownload()
      } catch (directError) {
        console.warn('❌ Direct download failed:', directError)
        
        try {
          // Then try fetch method
          await tryFetchDownload()
        } catch (fetchError) {
          console.warn('❌ Fetch download failed:', fetchError)
          
          // Finally use new tab fallback
          tryNewTabFallback()
        }
      }
      
    } catch (error) {
      console.error('❌ All download methods failed:', error)
      
      // Last resort: copy URL to clipboard
      try {
        await navigator.clipboard.writeText(imageUrl)
        alert('Download failed, but image URL copied to clipboard. You can paste it in a new tab to view and save the image.')
      } catch (clipboardError) {
        alert('Download failed. Please right-click the image and select "Save image as..." to download it manually.')
      }
    }
  }

  const handleRefresh = () => {
    if (user) {
      setLoading(true)
      loadUserImages()
    }
  }

  // Loading placeholder component
  const LoadingPlaceholder = () => (
    <div className="group bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Loading Image Placeholder */}
      <div className="aspect-square relative overflow-hidden bg-gray-100">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center space-y-3">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Generating...</p>
              {generationStatus && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2 px-2">
                  {generationStatus}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer"></div>
        
        {/* Model Badge Placeholder */}
        <div className="absolute top-2 left-2">
          <div className="px-2 py-1 bg-gray-300 animate-pulse rounded-full w-12 h-5"></div>
        </div>
      </div>

      {/* Metadata Placeholder */}
      <div className="p-4">
        {/* Prompt Placeholder */}
        <div className="space-y-2 mb-3">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
        </div>

        {/* Stats Placeholder */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-3 bg-gray-200 rounded animate-pulse w-8"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-8"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-8"></div>
          </div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading your generations...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    // Show newGenerations for anonymous users
    if (newGenerations.length === 0) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No generations yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first feet image to see it here as a thumbnail!
            </p>
            <p className="text-sm text-gray-500">
              You get 1 free generation per day. Sign up for unlimited generations.
            </p>
          </div>
        </div>
      )
    }

    // Show thumbnails for anonymous users
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Your Generations</h2>
          <div className="flex items-center space-x-3">
            <div className="text-sm text-gray-500">{newGenerations.length} images</div>
            <div className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
              Guest Mode
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-blue-400 rounded-full flex items-center justify-center">
                <span className="text-blue-800 text-xs font-bold">i</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800">Guest Mode</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your images are shown here temporarily. Sign up to save them permanently and get unlimited generations.
              </p>
            </div>
          </div>
        </div>

        {/* Show loading state for anonymous users */}
        {isGenerating && (
          <LoadingPlaceholder />
        )}

        {/* Grid of generated images for anonymous users */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newGenerations.map((image) => (
            <div key={image.id} className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              {/* Image */}
              <div className="aspect-square relative overflow-hidden bg-gray-100">
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  onClick={() => handleImageClick(image)}
                />
                
                {/* Model Badge */}
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-full backdrop-blur-sm">
                    {image.generation_settings?.model || 'AI'}
                  </span>
                </div>
                
                {/* Action Buttons */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDownload(image.image_url, image.title)
                      }}
                      className="p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors backdrop-blur-sm"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="p-4">
                {/* Prompt */}
                <div className="mb-3">
                  <p className="text-sm text-gray-900 font-medium line-clamp-2">
                    {image.prompt_text || image.description || 'Generated with filters'}
                  </p>
                  
                  {/* Filters Summary */}
                  {image.filters_used && getFilterSummary(image.filters_used) && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                      {getFilterSummary(image.filters_used)}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <Eye className="w-3 h-3" />
                      <span>{image.views_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Heart className="w-3 h-3" />
                      <span>{image.likes_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Download className="w-3 h-3" />
                      <span>{image.download_count || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(image.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal for anonymous users */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
              <div className="relative">
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
                
                {/* Close button */}
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                >
                  ×
                </button>
                
                {/* Download button */}
                <button
                  onClick={() => handleDownload(selectedImage.image_url, selectedImage.title)}
                  className="absolute bottom-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
              
              {/* Image details */}
              <div className="p-4 border-t">
                <h3 className="font-semibold text-gray-900 mb-2">{selectedImage.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{selectedImage.prompt_text || selectedImage.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Model: {selectedImage.generation_settings?.model || 'AI'}</span>
                  <span>{formatDate(selectedImage.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Your Generations</h2>
        <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-500">{images.length} images</div>
          {user && (
            <>
              {/* Database Status Indicator */}
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                dbStatus.isConnected && dbStatus.tablesExist && dbStatus.userProfileExists
                  ? 'bg-green-100 text-green-700'
                  : dbStatus.isConnected && dbStatus.tablesExist
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  dbStatus.isConnected && dbStatus.tablesExist && dbStatus.userProfileExists
                    ? 'bg-green-500' 
                    : dbStatus.isConnected && dbStatus.tablesExist
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`} />
                <span>
                  {dbStatus.isConnected && dbStatus.tablesExist && dbStatus.userProfileExists
                    ? 'DB Ready' 
                    : dbStatus.isConnected && dbStatus.tablesExist
                    ? 'Profile Setup'
                    : 'DB Issue'
                  }
                </span>
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                title="Refresh from database"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Database Status Message */}
      {user && (!dbStatus.isConnected || !dbStatus.userProfileExists) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-yellow-800 text-xs font-bold">!</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Database Connection Issue</h3>
              <div className="text-sm text-yellow-700 mt-1 space-y-1">
                <p>Your images are being saved temporarily but may not persist across sessions.</p>
                
                {!dbStatus.isConnected && (
                  <p>• Database tables are not accessible</p>
                )}
                
                {dbStatus.isConnected && !dbStatus.userProfileExists && (
                  <p>• User profile needs to be created</p>
                )}
                
                <p className="text-xs mt-2">
                  Auth Status: {dbStatus.authStatus}
                </p>
              </div>
              
              {dbStatus.error && (
                <p className="text-xs text-yellow-600 mt-2 font-mono bg-yellow-100 p-2 rounded">
                  Error: {dbStatus.error}
                </p>
              )}
              <div className="mt-3">
                <button
                  onClick={handleRefresh}
                  className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
                >
                  Retry Connection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generation Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Loading Placeholder - show when generating */}
        {isGenerating && <LoadingPlaceholder />}
        
        {images.map((image) => (
          <div
            key={image.id}
            className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer"
            onClick={() => handleImageClick(image)}
          >
            {/* Image */}
            <div className="aspect-square relative overflow-hidden">
              <img
                src={image.image_url}
                alt={image.description || 'Generated feet image'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
              
              {/* Overlay Actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownload(image.image_url, image.title)
                  }}
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    // Add share functionality
                  }}
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  title="Share"
                >
                  <Share className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    // Add more options
                  }}
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
                  title="More options"
                >
                  <MoreVertical className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Model Badge */}
              <div className="absolute top-2 left-2">
                <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-full">
                  {image.generation_settings?.model || 'AI'}
                </span>
              </div>
            </div>

            {/* Metadata */}
            <div className="p-4">
              {/* Prompt */}
              <p className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                {image.prompt_text || image.description || 'No prompt'}
              </p>

              {/* Filters */}
              {getFilterSummary(image.filters_used) && (
                <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                  {getFilterSummary(image.filters_used)}
                </p>
              )}

              {/* Stats and Time */}
              <div className="flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>{image.views_count || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Heart className="w-3 h-3" />
                    <span>{image.likes_count || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Download className="w-3 h-3" />
                    <span>{image.download_count || 0}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(image.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-hidden flex">
            {/* Image */}
            <div className="flex-1">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.description || 'Generated feet image'}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Details */}
            <div className="w-80 p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Details</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Prompt */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Prompt</label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedImage.prompt_text || selectedImage.description || 'No prompt'}
                </p>
              </div>

              {/* Model */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Model</label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedImage.generation_settings?.model || 'AI'}
                </p>
              </div>

              {/* Created */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Created</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(selectedImage.created_at).toLocaleString()}
                </p>
              </div>

              {/* Filters */}
              {selectedImage.filters_used && Object.keys(selectedImage.filters_used).length > 0 && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700">Filters Used</label>
                  <div className="mt-2 space-y-1">
                    {Object.entries(selectedImage.filters_used)
                      .filter(([_, value]) => value && value !== '')
                      .map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-gray-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                          </span>
                          <span className="text-gray-900 ml-2">{value as string}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={() => handleDownload(selectedImage.image_url, selectedImage.title)}
                  className="w-full flex items-center justify-center space-x-2 p-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 p-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  <Share className="w-4 h-4" />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GenerationHistory 